/*
  InStyle — size buttons on a product card switch the card in place.
  ------------------------------------------------------------------
  Naman, 2026-10-02: a bed's sizes should work "like how colors happen ... you
  can immediately see the difference", and "when you [select a size] the
  picture doesn't change".

  snippets/instyle-card.liquid prints one link per size (`[data-isc-size]`,
  "<product id>-<variant id>", href = the product page on that size) and, at
  the end of the card, a whole card copy per size in a
  `<template data-isc-sizecard>`: that size's own main photo, hover photo,
  swipe set, price, selected button and link. A tap swaps the card for that
  copy, as assets/instyle-card-colours.js does for a colour. A new card element
  means the swipe gallery (instyle-card-gallery.js) and the hover cycling
  (instyle-card-cycle.js) start fresh on the new photos, with nothing stale.

  The copies are kept in a page-wide cache by key, gathered as cards appear,
  because a colour swap rebuilds a card from a template with the templates
  stripped off; the slot's colour templates and colour group are carried over
  to the new card so the dots keep switching colours.

  No network. Without this script the sizes stay plain links. Events are
  delegated from the document; loaded site-wide from layout/theme.liquid.
*/
(function () {
  'use strict';
  if (window.InStyleCardSizes) return;

  var CARD = '.instyle-card[data-isc-product]';
  var SIZE = 'a.instyle-size[data-isc-size]';
  var cache = {}; // "productId-variantId" -> <template>

  function gather(root) {
    if (!root || !root.querySelectorAll) return;
    var tpls = root.querySelectorAll('template[data-isc-sizecard]');
    for (var i = 0; i < tpls.length; i++) {
      var key = tpls[i].getAttribute('data-isc-sizecard');
      if (!cache[key]) cache[key] = tpls[i];
      // A colour copy carries its own size copies inside its template content.
      gather(tpls[i].content);
    }
    var colours = root.querySelectorAll('template[data-isc-colour]');
    for (var j = 0; j < colours.length; j++) gather(colours[j].content);
  }

  function upgrade(root) {
    var sizes = (root || document).querySelectorAll(SIZE);
    for (var i = 0; i < sizes.length; i++) {
      var s = sizes[i];
      if (s.getAttribute('role') === 'button') continue;
      if (!cache[s.getAttribute('data-isc-size')]) continue;
      s.setAttribute('role', 'button');
      s.setAttribute('aria-pressed', s.getAttribute('aria-current') === 'true' ? 'true' : 'false');
      s.removeAttribute('aria-current');
    }
  }

  function select(size) {
    var card = size.closest(CARD);
    if (!card) return false;
    if (size.getAttribute('aria-pressed') === 'true') return true; // already showing this size
    var tpl = cache[size.getAttribute('data-isc-size')];
    if (!tpl || !tpl.content) return false;
    var next = document.importNode(tpl.content, true).querySelector(CARD);
    if (!next) return false;

    // Keep the slot's colours: its colour templates and the colour group.
    var keep = card.querySelectorAll(':scope > template[data-isc-colour], :scope > template[data-isc-sizecard]');
    for (var i = 0; i < keep.length; i++) next.appendChild(keep[i]);
    if (card._iscGroup) next._iscGroup = card._iscGroup;

    var key = size.getAttribute('data-isc-size');
    var hadFocus = document.activeElement === size;
    card.replaceWith(next);
    upgrade(next);
    if (window.InStyleCardColours && next.parentNode) window.InStyleCardColours.scan(next.parentNode);
    if (hadFocus) {
      var same = next.querySelector(SIZE + '[data-isc-size="' + key + '"]');
      if (same) same.focus({ preventScroll: true });
    }
    return true;
  }

  // A head start: pointing at a size (a finger too, pointerover comes before
  // the tap) fetches that size's main photo, so the new card is not grey.
  function warm(e) {
    var size = e.target.closest ? e.target.closest(SIZE) : null;
    if (!size || size._iscWarm) return;
    var tpl = cache[size.getAttribute('data-isc-size')];
    if (!tpl || !tpl.content) return;
    size._iscWarm = true;
    var img = tpl.content.querySelector('img.instyle-card__img');
    if (!img) return;
    var pre = new Image();
    if (img.getAttribute('sizes')) pre.sizes = img.getAttribute('sizes');
    if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
    pre.src = img.getAttribute('src');
  }

  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('focusin', warm);

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // a new tab still opens that size
    var size = e.target.closest ? e.target.closest(SIZE) : null;
    if (!size) return;
    if (select(size)) {
      e.preventDefault();
      e.stopPropagation();
    }
  });

  // Space activates a toggle button (Enter already clicks the link).
  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar') return;
    var size = e.target.closest ? e.target.closest(SIZE) : null;
    if (size && size.getAttribute('role') === 'button') {
      e.preventDefault();
      select(size);
    }
  });

  function scan(root) {
    gather(root || document);
    upgrade(root || document);
  }

  window.InStyleCardSizes = { scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }
  window.addEventListener('pageshow', function () {
    scan(document);
  });

  // Filters, sorting, pagination and colour swaps insert new cards.
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches(CARD)) scan(n.parentNode || n);
          else if (n.querySelector && n.querySelector(CARD)) scan(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
